import { useEffect } from 'react';

import {
  MessagingPlatform,
  SlackChannelType,
  UserCommunication,
  UserCommunicationRemoveBatchPayload,
  UserCommunicationRemovePayload,
  WebSocketEntityType,
} from '@this+that/models';
import log from 'loglevel';

import {
  useFetchIndividualUserCommunications,
  useInboxAccountFilter,
  useInboxFetchGroups,
} from '../../../hooks';
import { SlackMessageTypes } from '../../../models';
import {
  useCommunicationIntentsStore,
  useFetchCacheStore,
  useInboxEntriesStore,
  useIntegrationsStore,
  useSlackCommunicationsStore,
  useTeamsCommunicationsStore,
  useUserCommunicationsStore,
} from '../../../store';
import { CommunicationIntentKind } from '../../../store/Inbox/CommunicationIntents/types';
import { indexedDBAdapter } from '../../../store/utils/indexedDBAdapter';
import { INBOX_FEATURES_LIMIT } from '../../../utils/constants';
import {
  useWebSocket,
  WebSocketEventType,
} from '../../WebSocket/WebSocketContext';

const reconcileHydratedLabelsIntents = () => {
  const { intents, reconcileCommunication } =
    useCommunicationIntentsStore.getState();
  const { getUserCommunicationByMessageId, updateUserCommunicationLabels } =
    useUserCommunicationsStore.getState();

  Object.values(intents)
    .filter((intent) => intent.kind === CommunicationIntentKind.LABELS)
    .forEach((intent) => {
      const communication = getUserCommunicationByMessageId(intent.messageId);
      if (!communication) {
        return;
      }
      const { communication: reconciled, wasCorrected } =
        reconcileCommunication(communication);
      if (wasCorrected) {
        updateUserCommunicationLabels(
          intent.messageId,
          reconciled.labelIds ?? [],
        );
      }
    });
};

const HIGHLIGHT_DURATION_MS = 3000;
const insertHighlightTimeouts = new Map<
  string,
  ReturnType<typeof setTimeout>
>();

interface SlackRemoveBatchGroup {
  groupType: SlackMessageTypes;
  channel: string;
  messageIds: string[];
}

const groupSlackRemoveItemsByChannel = (
  items: UserCommunicationRemovePayload[],
): SlackRemoveBatchGroup[] => {
  const groups = new Map<string, SlackRemoveBatchGroup>();

  items.forEach(({ channel, channelType, messageId }) => {
    if (!channel) {
      return;
    }
    const existing = groups.get(channel);
    if (existing) {
      existing.messageIds.push(messageId);
      return;
    }
    const groupType =
      channelType === SlackChannelType.IM
        ? SlackMessageTypes.DIRECTS
        : SlackMessageTypes.CHANNELS;
    groups.set(channel, { groupType, channel, messageIds: [messageId] });
  });

  return Array.from(groups.values());
};

interface TeamsRemoveBatchGroup {
  chatId: string;
  messageIds: string[];
}

const groupTeamsRemoveItemsByChat = (
  items: UserCommunicationRemovePayload[],
): TeamsRemoveBatchGroup[] => {
  const groups = new Map<string, string[]>();

  items.forEach(({ channel, messageId }) => {
    const chatId = channel ?? '';
    const existing = groups.get(chatId);
    if (existing) {
      existing.push(messageId);
      return;
    }
    groups.set(chatId, [messageId]);
  });

  return Array.from(groups.entries()).map(([chatId, messageIds]) => ({
    chatId,
    messageIds,
  }));
};

/**
 * Hook to synchronize UserCommunications WebSocket events with the store
 *
 * Subscribes to:
 * - INSERT: Adds or updates communication (using removeAndInsert to avoid duplicates)
 * - MODIFY: Updates existing communication
 * - REMOVE: Removes communication by messageId
 * - REMOVE_BATCH: Removes many communications from one grouped payload
 * - RECONNECT_AFTER_LONG_PAUSE: Refetches all communications
 */
export function useUserCommunicationsSync() {
  const { subscribe } = useWebSocket();
  const {
    removeAndInsertUserCommunication,
    modifyUserCommunication,
    removeUserCommunicationByMessageId,
    addRecentlyInsertedId,
    removeRecentlyInsertedId,
  } = useUserCommunicationsStore();

  const { integrationAccounts, setCurrentEmailFilters } =
    useIntegrationsStore();
  const { fetchEmailFilters } = useInboxAccountFilter(integrationAccounts);

  useEffect(() => {
    setCurrentEmailFilters(fetchEmailFilters);
  }, [fetchEmailFilters, setCurrentEmailFilters]);

  // One-shot reconciliation for the F5 hydration race: UserCommunications and
  // CommunicationIntents persist independently, so whichever finishes first
  // may already be stale relative to the other. Runs once both have settled,
  // regardless of which one wins the race.
  useEffect(() => {
    let isUserCommunicationsHydrated =
      useUserCommunicationsStore.persist.hasHydrated();
    let isIntentsHydrated = useCommunicationIntentsStore.persist.hasHydrated();

    const runIfReady = () => {
      if (isUserCommunicationsHydrated && isIntentsHydrated) {
        reconcileHydratedLabelsIntents();
      }
    };

    runIfReady();

    const unsubscribeUserCommunicationsHydration =
      useUserCommunicationsStore.persist.onFinishHydration(() => {
        isUserCommunicationsHydrated = true;
        runIfReady();
      });
    const unsubscribeIntentsHydration =
      useCommunicationIntentsStore.persist.onFinishHydration(() => {
        isIntentsHydrated = true;
        runIfReady();
      });

    return () => {
      unsubscribeUserCommunicationsHydration();
      unsubscribeIntentsHydration();
    };
  }, []);

  const { addSlackCommunication, deleteSlackCommunication } =
    useSlackCommunicationsStore();

  const { addTeamsCommunication, deleteTeamsCommunication } =
    useTeamsCommunicationsStore();

  const { invalidateAllViewTypes } = useFetchCacheStore();

  const { handleFetchIndividualUserCommunications } =
    useFetchIndividualUserCommunications({
      limit: INBOX_FEATURES_LIMIT,
      isInitialFetch: true,
    });

  const { combinedViewTypes, separateViewTypes, nonCategoryViewTypes } =
    useInboxFetchGroups();

  useEffect(() => {
    log.debug('[UserCommunicationsSync] Setting up sync subscriptions');

    // Subscribe to RECONNECT_AFTER_LONG_PAUSE - refetch all communications
    const unsubscribeReconnect = subscribe(
      WebSocketEntityType.UserCommunication,
      WebSocketEventType.RECONNECT_AFTER_LONG_PAUSE,
      async () => {
        log.info(
          '[UserCommunicationsSync] Reconnected after long pause, invalidating cache and fetching communications',
        );

        invalidateAllViewTypes();

        await indexedDBAdapter.cleanupDeletedItems();

        if (fetchEmailFilters !== undefined && fetchEmailFilters.length === 0) {
          return;
        }

        await Promise.all([
          handleFetchIndividualUserCommunications(
            combinedViewTypes,
            undefined,
            fetchEmailFilters,
          ),
          ...separateViewTypes.map((vt) =>
            handleFetchIndividualUserCommunications(
              vt,
              undefined,
              fetchEmailFilters,
            ),
          ),
          ...nonCategoryViewTypes.map((vt) =>
            handleFetchIndividualUserCommunications(
              vt,
              undefined,
              fetchEmailFilters,
            ),
          ),
        ]);
      },
    );

    // Subscribe to INSERT events - use removeAndInsert to avoid duplicates
    const unsubscribeInsert = subscribe<UserCommunication>(
      WebSocketEntityType.UserCommunication,
      WebSocketEventType.INSERT,
      async ({ item }) => {
        if (!item) {
          return;
        }
        const { channelType, channel, messageId, platform } = item;
        log.debug('[UserCommunicationsSync] INSERT event received', messageId);
        if (platform === MessagingPlatform.SLACK) {
          const groupType =
            channelType === SlackChannelType.IM
              ? SlackMessageTypes.DIRECTS
              : SlackMessageTypes.CHANNELS;
          if (channel) {
            addSlackCommunication(groupType, channel, item, true);
          }
          return;
        }
        if (platform === MessagingPlatform.TEAMS) {
          addTeamsCommunication(channel ?? '', item, true);
          return;
        }

        const wasDeletedLocally =
          await indexedDBAdapter.isDeletedLocally(messageId);
        if (wasDeletedLocally) {
          return;
        }

        const { communication: reconciledItem } = useCommunicationIntentsStore
          .getState()
          .reconcileCommunication(item);

        removeAndInsertUserCommunication(reconciledItem);
        // The email INSERT carries the whole enriched thread; a DoBox showing it gets the new head too.
        useInboxEntriesStore
          .getState()
          .taskManager.activeTaskGroup.upsertEmailThreadHeadInInboxEntries(
            reconciledItem,
          );

        const existingTimeout = insertHighlightTimeouts.get(messageId);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        addRecentlyInsertedId(messageId);

        const timeoutId = setTimeout(() => {
          removeRecentlyInsertedId(messageId);
          insertHighlightTimeouts.delete(messageId);
        }, HIGHLIGHT_DURATION_MS);

        insertHighlightTimeouts.set(messageId, timeoutId);
      },
    );

    // Subscribe to MODIFY events - update existing communication
    const unsubscribeModify = subscribe<UserCommunication>(
      WebSocketEntityType.UserCommunication,
      WebSocketEventType.MODIFY,
      ({ item }) => {
        if (!item) {
          return;
        }
        log.debug(
          '[UserCommunicationsSync] MODIFY event received',
          item.messageId,
        );

        const { communication: reconciledItem } = useCommunicationIntentsStore
          .getState()
          .reconcileCommunication(item);
        modifyUserCommunication(reconciledItem);
      },
    );

    // Subscribe to REMOVE events - remove by messageId
    const unsubscribeRemove = subscribe<UserCommunication>(
      WebSocketEntityType.UserCommunication,
      WebSocketEventType.REMOVE,
      ({ item }) => {
        if (!item) {
          return;
        }

        const { channelType, channel, messageId, platform } = item;

        log.debug('[UserCommunicationsSync] REMOVE event received', messageId);

        // handle insert by platform
        if (platform === MessagingPlatform.SLACK) {
          const groupType =
            channelType === SlackChannelType.IM
              ? SlackMessageTypes.DIRECTS
              : SlackMessageTypes.CHANNELS;
          if (channel) {
            deleteSlackCommunication(groupType, channel, messageId);
          }
          return;
        }
        if (platform === MessagingPlatform.TEAMS) {
          deleteTeamsCommunication(channel ?? '', messageId);
          return;
        }

        removeUserCommunicationByMessageId(messageId);
        useCommunicationIntentsStore.getState().clearIntents([messageId]);
      },
    );

    // Subscribe to REMOVE_BATCH events - remove a grouped batch in one store write
    const unsubscribeRemoveBatch =
      subscribe<UserCommunicationRemoveBatchPayload>(
        WebSocketEntityType.UserCommunication,
        WebSocketEventType.REMOVE_BATCH,
        ({ item }) => {
          if (!item) {
            return;
          }

          const { platform, items } = item;

          log.debug(
            '[UserCommunicationsSync] REMOVE_BATCH event received',
            items.length,
          );

          if (platform === MessagingPlatform.SLACK) {
            groupSlackRemoveItemsByChannel(items).forEach(
              ({ groupType, channel, messageIds }) => {
                useSlackCommunicationsStore
                  .getState()
                  .deleteSlackCommunications(groupType, channel, messageIds);
              },
            );
            return;
          }

          if (platform === MessagingPlatform.TEAMS) {
            groupTeamsRemoveItemsByChat(items).forEach(
              ({ chatId, messageIds }) => {
                useTeamsCommunicationsStore
                  .getState()
                  .deleteTeamsCommunications(chatId, messageIds);
              },
            );
            return;
          }

          const messageIds = items.map((removedItem) => removedItem.messageId);
          useUserCommunicationsStore
            .getState()
            .removeUserCommunicationsByMessageIds(messageIds);
          useCommunicationIntentsStore.getState().clearIntents(messageIds);
        },
      );

    // Cleanup all subscriptions on unmount
    return () => {
      log.debug('[UserCommunicationsSync] Cleaning up sync subscriptions');
      unsubscribeReconnect();
      unsubscribeInsert();
      unsubscribeModify();
      unsubscribeRemove();
      unsubscribeRemoveBatch();
      insertHighlightTimeouts.forEach((timeout) => clearTimeout(timeout));
      insertHighlightTimeouts.clear();
    };
  }, [
    subscribe,
    handleFetchIndividualUserCommunications,
    removeAndInsertUserCommunication,
    modifyUserCommunication,
    removeUserCommunicationByMessageId,
    addRecentlyInsertedId,
    removeRecentlyInsertedId,
    combinedViewTypes,
    separateViewTypes,
    nonCategoryViewTypes,
    fetchEmailFilters,
  ]);
}
